/**
 * Détection d'une température de four dans le texte libre d'une étape.
 *
 * Symétrique d'`extractDuration` (US-17), et écrit pour la même raison : sur
 * une recette importée, `step.temperature_c` est vide alors que la température
 * est écrite en toutes lettres dans l'instruction. Sur les 29 recettes
 * reprises de CookBook, dix-neuf étapes mentionnent une température et aucune
 * ne la porte en base.
 *
 * **Mêmes règles directrices qu'`extractDuration`, et elles ne se négocient
 * pas :**
 *
 *   · **Ne jamais deviner.** Un nombre n'est une température que s'il porte un
 *     marqueur explicite : le degré, le mot « degrés », ou le thermostat. Un
 *     « 15 min » ou un « 200 g » ne doit jamais être lu comme 15 °C ou 200 °C.
 *     C'est l'inverse du problème d'`extractDuration`, qui devait au contraire
 *     retirer les mesures avant de chercher — ici, l'exigence d'un marqueur
 *     suffit et rend le retrait inutile.
 *   · **Cette fonction ne décide pas de ce qui entre en base.** Depuis US-78
 *     elle est appelée à l'enregistrement (`buildSteps`, `PUT .../steps`),
 *     mais la règle d'écriture — ne remplir que ce qui est vide, ne jamais
 *     reprendre une valeur touchée à la main — vit côté serveur, avec les
 *     colonnes d'origine de la migration `028`. `step.temperature_c`, quand
 *     il existe, reste prioritaire à l'affichage.
 *
 * **Le thermostat est une convention, pas une équivalence.** « Thermostat 5 »
 * vaut 150 °C par la règle française « numéro × 30 ». Elle est approximative —
 * les fours n'ont pas tous la même échelle — mais elle est universellement
 * admise et sans ambiguïté d'interprétation, contrairement à une quantité
 * d'ingrédient qu'il faudrait inventer. La conversion est donc faite, et
 * écrite ici plutôt que devinée ailleurs. **Écarté :** ignorer le thermostat
 * (laisserait sans température les recettes de livres anciens, qui ne parlent
 * qu'en thermostats).
 */

/**
 * Bornes de plausibilité. En deçà de 30 °C, il ne s'agit pas d'un four mais
 * d'une température ambiante ou d'un liquide tiède ; au-delà de 300 °C, aucun
 * four domestique ne suit — c'est plus probablement un nombre mal lu.
 */
const MIN_CELSIUS = 30;
const MAX_CELSIUS = 300;

/** Facteur de la convention française thermostat → degrés Celsius. */
const THERMOSTAT_FACTOR = 30;

/**
 * « 200°C », « 170 °C », « 160° », « 180 degrés », « 180 C ». Le `C` est
 * facultatif après le degré : une recette française qui écrit « 210° » ne
 * parle pas de Fahrenheit. La lookahead `(?![fF])` cède « 350 °F » au motif
 * Fahrenheit au lieu d'en lire le nombre comme des degrés Celsius — 350 °C
 * serait hors borne, et la fonction rendrait `null` au lieu de convertir.
 */
const CELSIUS = /(\d{1,3})\s*(?:°\s*(?![fF])C?|degr[ée]s?(?!\s*[fF]))/i;

/**
 * « 180 C », sans degré (US-78). Sensible à la casse et borné par `\b` : un
 * `C` majuscule isolé après un nombre est une température, « 30 cl » ou
 * « vitamine C » n'en sont pas.
 */
const CELSIUS_NU = /(\d{2,3})\s*C\b/;

/**
 * « 350 °F », « 350 degrés Fahrenheit », « 350 F » (US-78). La conversion est
 * déterministe — (°F − 32) × 5⁄9, arrondie au degré — et l'écarter reviendrait
 * à perdre toutes les recettes anglophones importées. La plausibilité se juge
 * **après** conversion : 350 °F vaut 177 °C, dans la plage d'un four.
 */
const FAHRENHEIT = /(\d{2,3})\s*(?:°\s*F\b|degr[ée]s?\s+Fahrenheit\b)/i;
const FAHRENHEIT_NU = /(\d{2,3})\s*F\b/;

/**
 * « thermostat 5 », « th. 6 », « th 7 ». Le point et l'espace sont
 * facultatifs. Bornée à un chiffre : il n'existe pas de thermostat 10 sur un
 * four domestique, et « th. 12 » serait plus probablement autre chose.
 */
const THERMOSTAT = /(?:thermostats?|th\.?)\s*(\d{1,2})\b/i;

function isPlausible(celsius: number): boolean {
  return Number.isFinite(celsius) && celsius >= MIN_CELSIUS && celsius <= MAX_CELSIUS;
}

/** (°F − 32) × 5⁄9, arrondi au degré. */
function fahrenheitVersCelsius(fahrenheit: number): number {
  return Math.round(((fahrenheit - 32) * 5) / 9);
}

/**
 * Température en degrés Celsius détectée dans l'instruction, ou `null` si rien
 * de sûr. Le degré Celsius prime sur le Fahrenheit et sur le thermostat : une
 * instruction qui donne les deux (« thermostat 6 (180 °C) ») est plus fiable
 * sur la valeur explicite — et on stocke en °C.
 */
export function extractTemperatureFromText(instruction: string): number | null {
  const celsius = CELSIUS.exec(instruction) ?? CELSIUS_NU.exec(instruction);
  if (celsius !== null) {
    const value = Number(celsius[1]);
    return isPlausible(value) ? value : null;
  }

  const fahrenheit = FAHRENHEIT.exec(instruction) ?? FAHRENHEIT_NU.exec(instruction);
  if (fahrenheit !== null) {
    const value = fahrenheitVersCelsius(Number(fahrenheit[1]));
    return isPlausible(value) ? value : null;
  }

  const thermostat = THERMOSTAT.exec(instruction);
  if (thermostat !== null) {
    const value = Number(thermostat[1]) * THERMOSTAT_FACTOR;
    return isPlausible(value) ? value : null;
  }

  return null;
}

/**
 * La température à **afficher** pour une étape — même garde que
 * `dureeAffichee` : une colonne vidée à la main (`'manuel'`) ne retombe pas
 * sur la lecture du texte, le gel de D11 vaut aussi pour l'affichage.
 */
export function temperatureAffichee(step: {
  temperature_c: number | null;
  temperature_source: 'auto' | 'manuel' | null;
  instruction: string;
}): number | null {
  if (step.temperature_c !== null) return step.temperature_c;
  if (step.temperature_source === 'manuel') return null;
  return extractTemperatureFromText(step.instruction);
}
